import React, { useState, useEffect, useRef } from 'react';
import Header from 'components/Header/Header';
import {
  faUsers,
  faSync,
  faPlus,
  faSearch,
  faEdit,
  faTrash,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Styles from './Grupos.module.scss';
import Switch from 'components/Template/Switch/Switch';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Creators } from 'store/ducks/grupos';
import { Creators as PermissoesCreators } from 'store/ducks/permissoes';
import { T, C } from 'shared/helpers';
import WithPermission from 'components/Template/WithPermission/WithPermission';

const Table = ({
  groups = [],
  onRemove = console.log,
  onEdit = console.log,
  onView = console.log,
  availablePermissions = [],
}) => (
  <div className={Styles.Table}>
    <div className={Styles.TableHeader}>
      <div className={Styles.TableRow}>
        <div>
          <input type='checkbox' disabled />
        </div>
        <div>Nome</div>
        <div>Descrição</div>
        <div>Permissões</div>
        <div>Ações</div>
      </div>
    </div>
    <div className={Styles.TableBody}>
      {groups.map(g => (
        <div className={Styles.TableRow}>
          <div>
            <input type='checkbox' onChange={() => {}} />
          </div>
          <div>{g.name}</div>
          <div>{g.description}</div>
          <div>
            {g.permissions
              .filter(p => availablePermissions.find(p2 => p2.id === p.id))
              .map(p => (
                <div className={Styles.Tag}>{p.name}</div>
              ))}
          </div>
          <div className={Styles.Controls}>
            <button onClick={() => onView(g)}>
              <FontAwesomeIcon icon={faSearch} />
              &nbsp; Detalhes
            </button>
            <WithPermission permission='roles.update'>
              <button onClick={() => onEdit(g)}>
                <FontAwesomeIcon icon={faEdit} />
                &nbsp; Editar
              </button>
            </WithPermission>
            <WithPermission permission='roles.delete'>
              <button onClick={() => onRemove(g)}>
                <FontAwesomeIcon icon={faTrash} />
                &nbsp; Deletar
              </button>
            </WithPermission>
          </div>
        </div>
      ))}
    </div>
  </div>
);
Table.propTypes = {
  groups: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  availablePermissions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

const GroupEditor = ({
  viewOnly,
  open,
  availablePermissions,
  group,
  onChangedGroup,
  onClose,
}) => {
  const [currGroup, onChangeGroup] = useState(group);

  const block = useRef(false);
  function _onChangedGroup(...args) {
    if (block.current) return;
    block.current = true;
    onChangedGroup(...args);
  }
  useEffect(() => {
    if (open) block.current = false;
  }, [open]);
  useEffect(() => {
    onChangeGroup(group);
  }, [group]);

  return (
    <div className={C(Styles.GroupEditor, [open, Styles.Open])}>
      <main>
        <header>
          Criar um novo grupo
          <div id='group_close_btn'>
            <FontAwesomeIcon icon={faTimes} onClick={onClose} />
          </div>
        </header>
        <form>
          <div>
            <p>Nome</p>
            {viewOnly ? (
              T(currGroup).name
            ) : (
              <input
                key={T(currGroup).id}
                defaultValue={T(currGroup).name}
                onChange={({ target: { value: name } }) =>
                  onChangeGroup({ ...currGroup, name })
                }
              />
            )}
          </div>
          <div>
            <p>Descrição</p>
            {viewOnly ? (
              T(currGroup).description
            ) : (
              <input
                key={T(currGroup).id}
                defaultValue={T(currGroup).description}
                onChange={({ target: { value: description } }) =>
                  onChangeGroup({ ...currGroup, description })
                }
              />
            )}
          </div>
          {availablePermissions.map(permission => (
            <div key={permission.id} className={Styles.PermissionSwitch}>
              <div>{permission.description}</div>
              <div>
                <Switch
                  className={Styles.Switch}
                  disabled={viewOnly}
                  active={
                    !!T(T(currGroup).permissions, []).find(
                      p => p.name === permission.name,
                    )
                  }
                  onChange={active => {
                    onChangeGroup({
                      ...currGroup,
                      permissions: active
                        ? T(T(currGroup).permissions, []).concat(permission)
                        : T(T(currGroup).permissions, []).filter(
                            p => p.name !== permission.name,
                          ),
                    });
                  }}
                />
              </div>
            </div>
          ))}
          {!viewOnly && (
            <button
              type='button'
              className={Styles.Save}
              onClick={() => _onChangedGroup(currGroup)}
              id='btn_salvar_grupo'
            >
              Salvar
            </button>
          )}
        </form>
      </main>
    </div>
  );
};
GroupEditor.propTypes = {
  viewOnly: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  availablePermissions: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  group: PropTypes.shape({
    permissions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
  }).isRequired,
  onChangedGroup: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function Grupos() {
  const dispatch = useDispatch();
  const groups = useSelector(state => state.grupos.data);
  const permissions = useSelector(state => state.permissoes.data);

  useEffect(() => {
    dispatch(Creators.getGrupos());
    dispatch(PermissoesCreators.getPermissoes());
  }, [dispatch]);

  const [selectedGroup, setSelectedGroup] = useState(undefined);

  const [editOpen, setEditOpen] = useState(false);
  function openEditScreen(group) {
    setSelectedGroup(group);
    setEditOpen(true);
  }

  const [viewOpen, setViewOpen] = useState(false);
  function openViewScreen(group) {
    setSelectedGroup(group);
    setViewOpen(true);
  }

  async function openRemoveModal(group) {
    // eslint-disable-next-line no-alert
    const confirma = window.confirm(
      `Confirmar a remoção do grupo ${group.name}`,
    );
    if (confirma) {
      dispatch(Creators.deleteGrupo(group));
    }
  }

  function onUpdateGroup(group) {
    if (group.id) {
      dispatch(Creators.updateGrupo(group));
    } else {
      dispatch(Creators.createGrupo(group));
    }
    setEditOpen(false);
    setSelectedGroup(undefined);
  }

  function onUpdatePermissoes() {
    dispatch(PermissoesCreators.updatePermissoes());
  }

  return (
    <div className={Styles.GruposContainer}>
      <GroupEditor
        availablePermissions={permissions}
        group={selectedGroup}
        onChangedGroup={onUpdateGroup}
        open={viewOpen || editOpen}
        viewOnly={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setEditOpen(false);
        }}
      />
      <Header icon={faUsers} title={'Listagem de grupos'} />
      <div className={Styles.Controls}>
        <WithPermission permission='roles.create'>
          <button className={Styles.Add} onClick={() => openEditScreen({})}>
            <FontAwesomeIcon icon={faPlus} />
            &nbsp; Criar
          </button>
        </WithPermission>
        <WithPermission permission='roles.update-permissions'>
          <button className={Styles.Sync} onClick={onUpdatePermissoes}>
            <FontAwesomeIcon icon={faSync} spin />
            &nbsp; Sincronizar permissões
          </button>
        </WithPermission>
      </div>
      <div className={Styles.Content}>
        <Table
          availablePermissions={permissions}
          groups={groups}
          onEdit={openEditScreen}
          onRemove={openRemoveModal}
          onView={openViewScreen}
        />
      </div>
    </div>
  );
}
